/**
 * SEO Utilities
 * Helpers for generating structured data (JSON-LD) and managing meta tags
 */

/**
 * Generate Organization structured data
 */
export function generateOrganizationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'FlightCents',
        url: 'https://flightcents.app',
        logo: 'https://flightcents.app/icons/flightcents.png',
        sameAs: [
            'https://twitter.com/flightcents',
            'https://facebook.com/flightcents',
            'https://instagram.com/flightcents'
        ],
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: '',
            contactType: 'customer support',
            email: 'support@flightcents.app'
        }
    };
}

/**
 * Generate SoftwareApplication structured data
 */
export function generateSoftwareSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'FlightCents',
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'Web',
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
        },
        description: 'Flight training cost tracker and budget management tool for student pilots.',
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            ratingCount: '150'
        }
    };
}

/**
 * Generate FAQ structured data
 */
export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            }
        }))
    };
}

/**
 * Helper to inject JSON-LD into the head
 */
export function injectJsonLd(schema: Record<string, any>) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
        document.head.removeChild(script);
    };
}

/**
 * Update meta tags dynamically
 */
export function updateMetaTags(tags: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
}) {
    if (tags.title) {
        document.title = `${tags.title} | FlightCents`;
        setMetaTag('property', 'og:title', tags.title);
        setMetaTag('name', 'twitter:title', tags.title);
    }

    if (tags.description) {
        setMetaTag('name', 'description', tags.description);
        setMetaTag('property', 'og:description', tags.description);
        setMetaTag('name', 'twitter:description', tags.description);
    }

    if (tags.image) {
        setMetaTag('property', 'og:image', tags.image);
        setMetaTag('name', 'twitter:image', tags.image);
    }

    if (tags.url) {
        setMetaTag('property', 'og:url', tags.url);
        setMetaTag('name', 'twitter:url', tags.url);
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
            canonical.setAttribute('href', tags.url);
        }
    }
}

function setMetaTag(attr: string, key: string, content: string) {
    let element = document.querySelector(`meta[${attr}="${key}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
}
